import React, {
  useState,
} from "react"

import useTitle
  from "@/hooks/useTitle"

import PulseLoader
  from "react-spinners/PulseLoader"

import {
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  // 09 Sep 2026
  PlusIcon,
  MinusIcon,
} from "@heroicons/react/24/outline"

import {
  useGetOrgIntentionBoardByIdQuery,
} from "@/features/orgIntentionBoards/orgIntentionBoardsApiSlice"

import EtOrgIntentionResponseBoard
  from "@/features/orgIntentionBoards/EtOrgIntentionResponseBoard"

import EtOrgIntentionCandidateReviewBoard
  from "@/features/orgIntentionBoards/EtOrgIntentionCandidateReviewBoard"

import EtOrgIgniterIntentionQuestionViewer
  from "./EtOrgIgniterIntentionQuestionViewer"

import EtOrgIgniterIntentionCandidateManager
  from "./EtOrgIgniterIntentionCandidateManager"

// 09 Sep 2026
import EtOrgIntentionBoardResponsePreview
  from "@/features/orgIntentionBoards/EtOrgIntentionBoardResponsePreview"


const EtViewOrgIgniterIntentionBoard = ({
  id,
  setActiveAdminView,
}) => {

  // First Created - 08 Sep 2026
  // Author - Atul Pandey
  // Purpose - Organization Igniter workspace for viewing and supporting
  //           a Trinity Intention Board.
  //
  // Org Igniters may:
  //
  // - view Intention Board information
  // - view questions
  // - manage eligible candidate nominations
  // - view submitted responses
  // - submit reviewer recommendations
  //
  // Org Igniters may NOT modify Intention Board configuration.
  // Updated - 09 Sep 2026 - Response Board Preview

  useTitle(
    "Intention Board"
  )

  /*
  |--------------------------------------------------------------------------
  | 01 - State
  |--------------------------------------------------------------------------
  */

  const [
    activeTab,
    setActiveTab,
  ] =
    useState(
      "overview"
    )

  /*
  |--------------------------------------------------------------------------
  | Board Narrative Expansion - 09 Sep 2026
  |--------------------------------------------------------------------------
  */

  const [
    showFullBoardNarrative,
    setShowFullBoardNarrative,
  ] =
    useState(
      false
    )

  /*
  |--------------------------------------------------------------------------
  | 02 - Get Intention Board
  |--------------------------------------------------------------------------
  */

  const {
    data:
      orgIntentionBoard,

    isLoading,

    isFetching,

    isError,

    error,
  } =
    useGetOrgIntentionBoardByIdQuery(
      {
        intentionBoardId:
          id,
      },
      {
        skip:
          !id,
      }
    )

  /*
  |--------------------------------------------------------------------------
  | 03 - Back To Intention Boards
  |--------------------------------------------------------------------------
  */

  const handleBack =
    () => {

      if (
        setActiveAdminView
      ) {

        setActiveAdminView(
          "orgIgniterIntentionBoards"
        )

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 04 - Copy Public URL
  |--------------------------------------------------------------------------
  */

  const handleCopyPublicUrl =
    async () => {

      if (
        !orgIntentionBoard
          ?.publicSlug
      ) {

        return

      }

      const publicUrl =
        `${window.location.origin}/trinity/intention/${orgIntentionBoard.publicSlug}`

      await navigator
        .clipboard
        .writeText(
          publicUrl
        )

    }

  /*
  |--------------------------------------------------------------------------
  | 05 - Format Date
  |--------------------------------------------------------------------------
  */

  const formatDateTime =
    (
      value,
      timezone
    ) => {

      if (
        !value
      ) {

        return "Not Set"

      }

      const date =
        new Date(
          value
        )

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {

        return "Not Set"

      }

      try {

        return new Intl.DateTimeFormat(
          "en-US",
          {
            timeZone:
              timezone ||
              "America/Los_Angeles",

            year:
              "numeric",

            month:
              "short",

            day:
              "numeric",

            hour:
              "numeric",

            minute:
              "2-digit",
          }
        )
          .format(
            date
          )

      } catch (
        error
      ) {

        return date
          .toLocaleString()

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 06 - Board Status Class
  |--------------------------------------------------------------------------
  */

  const getBoardStatusClass =
    boardStatus => {

      switch (
        boardStatus
      ) {

        case "open":

          return "bg-lime-100 text-lime-700"

        case "closed":

          return "bg-gray-200 text-gray-700"

        case "archived":

          return "bg-purple-100 text-purple-700"

        case "draft":
        default:

          return "bg-orange-100 text-orange-700"

      }

    }
  
  // 6b - Create an object for setting inner HTML - 09 Sep 2026

  const createMarkup =
    htmlContent => {

        return {
        __html:
            htmlContent,
        }

    }

  /*
  |--------------------------------------------------------------------------
  | 07 - Loading
  |--------------------------------------------------------------------------
  */

  if (
    isLoading
  ) {

    return (

      <div className="bg-white rounded m-1 p-4">

        <PulseLoader
          color={"#ea580c"}
          size={7}
        />

      </div>

    )

  }

  /*
  |--------------------------------------------------------------------------
  | 08 - Error
  |--------------------------------------------------------------------------
  */

  if (
    isError
  ) {

    return (

      <div className="bg-orange-50 rounded m-1 p-4">

        <p className="text-sm font-bold text-orange-700">
          Unable to load Intention Board
        </p>

        <p className="text-xs text-orange-700 mt-1">

          {
            error
              ?.data
              ?.message ||
            "The Intention Board could not be loaded."
          }

        </p>

        <button
          type="button"
          onClick={
            handleBack
          }
          className="text-xs font-bold text-orange-600 mt-3"
        >
          Back to Intention Boards
        </button>

      </div>

    )

  }

  /*
  |--------------------------------------------------------------------------
  | 09 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-gray-200 p-1 mx-auto rounded-lg font-poppins">

      {/*
      |--------------------------------------------------------------------------
      | Header
      |--------------------------------------------------------------------------
      */}

      <div className="bg-white m-1 p-3 rounded">

        <div className="flex justify-between items-start">

          <div className="flex justify-start items-start">

            <button
              type="button"
              onClick={
                handleBack
              }
              className="mr-3 mt-1 rounded-full h-7 w-7 flex justify-center items-center hover:bg-orange-50"
              title="Back to Intention Boards"
            >

              <ArrowLeftIcon className="h-4 w-4 text-orange-600" />

            </button>

            <div>

              <div className="flex justify-start items-center flex-wrap">

                <p className="text-2xl font-bold text-gray-800">

                  {
                    orgIntentionBoard
                      ?.boardName ||
                    "Intention Board"
                  }

                </p>

                <p
                  className={`text-[10px] font-bold rounded-full px-2 py-1 ml-2 ${
                    getBoardStatusClass(
                      orgIntentionBoard
                        ?.boardStatus
                    )
                  }`}
                >

                  {
                    orgIntentionBoard
                      ?.boardStatus ||
                    "draft"
                  }

                </p>

                <p
                  className={`text-[10px] font-bold rounded-full px-2 py-1 ml-1 ${
                    orgIntentionBoard
                      ?.isActive
                      ? "bg-lime-100 text-lime-700"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >

                  {
                    orgIntentionBoard
                      ?.isActive
                      ? "Active"
                      : "Inactive"
                  }

                </p>

              </div>

              <p className="text-sm text-gray-600 mt-1">

                {
                  orgIntentionBoard
                    ?.headline ||
                  "Leadership Intention Board"
                }

              </p>

            </div>

          </div>

          {isFetching && (

            <PulseLoader
              color={"#ea580c"}
              size={4}
            />

          )}

        </div>

      </div>

      {/*
      |--------------------------------------------------------------------------
      | Tabs
      |--------------------------------------------------------------------------
      */}

      <div className="bg-white m-1 p-1 rounded">

        <div className="flex justify-start items-center border-b border-gray-200 overflow-x-auto">

          {/*
          |--------------------------------------------------------------------------
          | Overview
          |--------------------------------------------------------------------------
          */}

          <div
            onClick={
              () =>
                setActiveTab(
                  "overview"
                )
            }
            className={`cursor-pointer px-4 py-2 text-xs font-bold whitespace-nowrap ${
              activeTab ===
              "overview"
                ? "text-orange-700 border-b-2 border-orange-600 bg-orange-50"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Overview
          </div>

          {/* Response Board Preview  09 Sep 2026*/}

          <div
            onClick={
              () =>
                setActiveTab(
                  "preview"
                )
            }
            className={`cursor-pointer px-4 py-2 text-xs font-bold ${
              activeTab ===
              "preview"
                ? "text-orange-700 border-b-2 border-orange-600 bg-orange-50"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Preview
          </div>

          {/*
          |--------------------------------------------------------------------------
          | Questions
          |--------------------------------------------------------------------------
          */}

          <div
            onClick={
              () =>
                setActiveTab(
                  "questions"
                )
            }
            className={`cursor-pointer px-4 py-2 text-xs font-bold whitespace-nowrap ${
              activeTab ===
              "questions"
                ? "text-orange-700 border-b-2 border-orange-600 bg-orange-50"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Questions
          </div>

          {/*
          |--------------------------------------------------------------------------
          | Candidates
          |--------------------------------------------------------------------------
          */}

          <div
            onClick={
              () =>
                setActiveTab(
                  "candidates"
                )
            }
            className={`cursor-pointer px-4 py-2 text-xs font-bold whitespace-nowrap ${
              activeTab ===
              "candidates"
                ? "text-orange-700 border-b-2 border-orange-600 bg-orange-50"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Candidates
          </div>

          {/*
          |--------------------------------------------------------------------------
          | Responses
          |--------------------------------------------------------------------------
          */}

          <div
            onClick={
              () =>
                setActiveTab(
                  "responses"
                )
            }
            className={`cursor-pointer px-4 py-2 text-xs font-bold whitespace-nowrap ${
              activeTab ===
              "responses"
                ? "text-orange-700 border-b-2 border-orange-600 bg-orange-50"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Responses
          </div>

          {/*
          |--------------------------------------------------------------------------
          | Reviews & Decisions
          |--------------------------------------------------------------------------
          */}

          <div
            onClick={
              () =>
                setActiveTab(
                  "reviews"
                )
            }
            className={`cursor-pointer px-4 py-2 text-xs font-bold whitespace-nowrap ${
              activeTab ===
              "reviews"
                ? "text-orange-700 border-b-2 border-orange-600 bg-orange-50"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            Reviews & Decisions
          </div>

        </div>

      </div>

      {/*
      |--------------------------------------------------------------------------
      | Overview
      |--------------------------------------------------------------------------
      */}

      {activeTab ===
        "overview" && (

        <div className="m-1 p-1">

          {/*
          |--------------------------------------------------------------------------
          | Board Narrative
          |--------------------------------------------------------------------------
          */}

          <div className="bg-white rounded m-1 p-4">

            <p className="text-sm font-bold text-gray-800">
              Intention Board
            </p>

            <p className="text-[10px] text-gray-500 mt-1">
              Read only board information for organization leaders supporting this Intention Board.
            </p>

            {/*
            |--------------------------------------------------------------------------
            | Board Narrative - Rich Text with Show / Hide - 09 Sep 2026
            |--------------------------------------------------------------------------
            */}

            {orgIntentionBoard
              ?.boardNarrative && (

              <div className="bg-gray-50 rounded mt-4 p-3">

                {/*
                |--------------------------------------------------------------------------
                | Header
                |--------------------------------------------------------------------------
                */}

                <div className="flex justify-start items-center mb-3">

                  <div
                    onClick={
                      () =>
                        setShowFullBoardNarrative(
                          prev =>
                            !prev
                        )
                    }
                    className="
                      h-7
                      w-7
                      rounded-full
                      flex
                      justify-center
                      items-center
                      cursor-pointer
                      hover:bg-gray-100
                    "
                    title={
                      showFullBoardNarrative
                        ? "Show less"
                        : "Show more"
                    }
                  >

                    {showFullBoardNarrative ? (

                      <MinusIcon
                        className="h-5 w-5 text-orange-600"
                      />

                    ) : (

                      <PlusIcon
                        className="h-5 w-5 text-orange-600"
                      />

                    )}

                  </div>

                  <p className="text-xs font-bold text-gray-800 mx-2">
                    Before You Begin
                  </p>

                </div>

                {/*
                |--------------------------------------------------------------------------
                | Narrative
                |--------------------------------------------------------------------------
                */}

                <div>

                  <div
                    className={`
                      text-xs
                      text-gray-700
                      leading-5
                      [&_p]:mb-2
                      [&_ul]:list-disc
                      [&_ul]:ml-5
                      [&_ul]:mb-2
                      [&_ol]:list-decimal
                      [&_ol]:ml-5
                      [&_ol]:mb-2
                      [&_li]:mb-1
                      [&_strong]:font-bold
                      [&_a]:text-orange-600
                      [&_a]:underline
                      ${
                        showFullBoardNarrative
                          ? ""
                          : "line-clamp-3"
                      }
                    `}
                    dangerouslySetInnerHTML={
                      createMarkup(
                        orgIntentionBoard
                          .boardNarrative
                      )
                    }
                  />

                  {/*
                  |--------------------------------------------------------------------------
                  | Read More / Less
                  |--------------------------------------------------------------------------
                  */}

                  <div className="flex justify-end items-center mt-2">

                    <button
                      type="button"
                      onClick={
                        () =>
                          setShowFullBoardNarrative(
                            prev =>
                              !prev
                          )
                      }
                      className="
                        text-xs
                        font-bold
                        text-orange-600
                        hover:text-orange-700
                        hover:underline
                        cursor-pointer
                      "
                    >

                      {
                        showFullBoardNarrative
                          ? "Read Less"
                          : "Read More"
                      }

                    </button>

                  </div>

                </div>

              </div>

            )}

            {/* {orgIntentionBoard
              ?.boardNarrative && (

              <div className="bg-gray-50 rounded mt-4 p-3">

                <p className="text-xs text-gray-700 whitespace-pre-wrap leading-5">

                  {
                    orgIntentionBoard
                      .boardNarrative
                  }

                </p>

              </div>

            )} */}

          </div>

          {/*
          |--------------------------------------------------------------------------
          | Board Information
          |--------------------------------------------------------------------------
          */}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 m-1">

            {/*
            |--------------------------------------------------------------------------
            | Response Deadline
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white rounded p-4">

              <p className="text-[9px] uppercase font-bold text-gray-500">
                Response Deadline
              </p>

              <p className="text-sm font-bold text-gray-800 mt-2">

                {
                  formatDateTime(
                    orgIntentionBoard
                      ?.responseDeadline,

                    orgIntentionBoard
                      ?.timezone
                  )
                }

              </p>

              <p className="text-[9px] text-gray-500 mt-1">

                {
                  orgIntentionBoard
                    ?.timezone ||
                  "America/Los_Angeles"
                }

              </p>

            </div>

            {/*
            |--------------------------------------------------------------------------
            | Response Window
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white rounded p-4">

              <p className="text-[9px] uppercase font-bold text-gray-500">
                Response Window Opened
              </p>

              <p className="text-sm font-bold text-gray-800 mt-2">

                {
                  orgIntentionBoard
                    ?.responseOpenedAt
                    ? formatDateTime(
                        orgIntentionBoard
                          .responseOpenedAt,

                        orgIntentionBoard
                          ?.timezone
                      )
                    : "Not Opened"
                }

              </p>

            </div>

            {/*
            |--------------------------------------------------------------------------
            | Late Responses
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white rounded p-4">

              <p className="text-[9px] uppercase font-bold text-gray-500">
                Late Responses
              </p>

              <p className="text-sm font-bold text-gray-800 mt-2">

                {
                  orgIntentionBoard
                    ?.allowLateResponses
                    ? "Allowed"
                    : "Not Allowed"
                }

              </p>

            </div>

            {/*
            |--------------------------------------------------------------------------
            | Organization Code
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white rounded p-4">

              <p className="text-[9px] uppercase font-bold text-gray-500">
                Organization Code
              </p>

              <p className="text-sm font-bold text-gray-800 mt-2">

                {
                  orgIntentionBoard
                    ?.orgCode ||
                  "Not Set"
                }

              </p>

            </div>

            {/*
            |--------------------------------------------------------------------------
            | Board Status
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white rounded p-4">

              <p className="text-[9px] uppercase font-bold text-gray-500">
                Board Status
              </p>

              <div className="mt-2">

                <p
                  className={`inline-block text-[10px] font-bold rounded-full px-2 py-1 ${
                    getBoardStatusClass(
                      orgIntentionBoard
                        ?.boardStatus
                    )
                  }`}
                >

                  {
                    orgIntentionBoard
                      ?.boardStatus ||
                    "draft"
                  }

                </p>

              </div>

            </div>

            {/*
            |--------------------------------------------------------------------------
            | Public Access
            |--------------------------------------------------------------------------
            */}

            <div className="bg-white rounded p-4">

              <p className="text-[9px] uppercase font-bold text-gray-500">
                Public Candidate Access
              </p>

              <div className="flex justify-start items-center mt-2">

                <p className="text-[10px] text-orange-700 break-all">

                  {
                    window.location.origin
                  }/trinity/intention/{
                    orgIntentionBoard
                      ?.publicSlug
                  }

                </p>

                <button
                  type="button"
                  onClick={
                    handleCopyPublicUrl
                  }
                  className="ml-2 rounded-full h-7 w-7 flex justify-center items-center hover:bg-orange-50"
                  title="Copy public URL"
                >

                  <ClipboardDocumentIcon className="h-4 w-4 text-orange-600" />

                </button>

              </div>

            </div>

          </div>

          {/*
          |--------------------------------------------------------------------------
          | Closing Narrative
          |--------------------------------------------------------------------------
          */}

          {orgIntentionBoard
            ?.closingNarrative && (

            <div className="bg-white rounded m-1 p-4">

              <p className="text-sm font-bold text-gray-800">
                Closing Narrative
              </p>

              <div className="bg-gray-50 rounded mt-3 p-3">

                <p className="text-xs text-gray-700 whitespace-pre-wrap leading-5">

                  {
                    orgIntentionBoard
                      .closingNarrative
                  }

                </p>

              </div>

            </div>

          )}

          {/*
          |--------------------------------------------------------------------------
          | Invitation Content
          |--------------------------------------------------------------------------
          */}

          {(orgIntentionBoard
              ?.invitationEmailSubject ||
            orgIntentionBoard
              ?.invitationEmailBody) && (

            <div className="bg-white rounded m-1 p-4">

              <p className="text-sm font-bold text-gray-800">
                Candidate Invitation
              </p>

              {orgIntentionBoard
                ?.invitationEmailSubject && (

                <div className="mt-3">

                  <p className="text-[9px] font-bold uppercase text-gray-500">
                    Subject
                  </p>

                  <p className="text-xs text-gray-700 mt-1">

                    {
                      orgIntentionBoard
                        .invitationEmailSubject
                    }

                  </p>

                </div>

              )}

              {orgIntentionBoard
                ?.invitationEmailBody && (

                <div className="mt-3">

                  <p className="text-[9px] font-bold uppercase text-gray-500">
                    Message
                  </p>

                  <div className="bg-gray-50 rounded mt-1 p-3">

                    <p className="text-xs text-gray-700 whitespace-pre-wrap leading-5">

                      {
                        orgIntentionBoard
                          .invitationEmailBody
                      }

                    </p>

                  </div>

                </div>

              )}

            </div>

          )}

        </div>

      )}

      {/* Response Board Preview  - 09 Sep 2026*/}

      {activeTab ===
        "preview" && (

        <div className="m-1 p-1">

          <EtOrgIntentionBoardResponsePreview
            board={
              orgIntentionBoard
            }
          />

        </div>

      )}

      {/*
      |--------------------------------------------------------------------------
      | Questions
      |--------------------------------------------------------------------------
      |
      | We will replace this with the dedicated read only
      | EtOrgIgniterIntentionQuestionViewer next.
      |
      */}

      {activeTab ===
        "questions" && (

        <div className="m-1 p-1">

            <EtOrgIgniterIntentionQuestionViewer
            intentionBoardId={
                id
            }
            />

        </div>

      )}

      {/*
      |--------------------------------------------------------------------------
      | Candidates
      |--------------------------------------------------------------------------
      |
      | We will replace this with the dedicated Org Igniter candidate manager.
      |
      */}

      {activeTab ===
        "candidates" && (

        <div className="m-1 p-1">

            <EtOrgIgniterIntentionCandidateManager
            intentionBoardId={
                id
            }
            />

        </div>

       )}

      {/*
      |--------------------------------------------------------------------------
      | Responses
      |--------------------------------------------------------------------------
      */}

      {activeTab ===
        "responses" && (

        <EtOrgIntentionResponseBoard
          intentionBoardId={
            id
          }
        />

      )}

      {/*
      |--------------------------------------------------------------------------
      | Reviews & Decisions
      |--------------------------------------------------------------------------
      */}

      {activeTab ===
        "reviews" && (

        <EtOrgIntentionCandidateReviewBoard
          intentionBoardId={
            id
          }
        />

      )}

    </div>

  )

}

export default EtViewOrgIgniterIntentionBoard